/**
 * The audit log's source IP filter: one address (or a CIDR block such as
 * 10.0.0.0/24), or a from / to range. The value is what the API's
 * `source_ip` takes: "10.0.0.5", "10.0.0.0/24" or "10.0.0.1-10.0.0.50";
 * the server does the real matching and validation.
 */

import { useId } from 'react';
import { ChevronDown, Network } from 'lucide-react';

import { usePopover } from '@/ui/usePopover';

import { type SourceIpDraft, useSourceIpDraft } from './useSourceIpDraft';

function SourceIpForm({
  draft,
  value,
  onApply,
}: {
  draft: SourceIpDraft;
  value: string;
  onApply: (value: string) => void;
}) {
  return (
    <form
      className="filter-popover-custom"
      onSubmit={(e) => {
        e.preventDefault();
        if (draft.valid) onApply(draft.result());
      }}
    >
      <div className="segmented" role="radiogroup" aria-label="Match">
        <button type="button" role="radio" aria-checked={draft.mode === 'single'} onClick={() => draft.setMode('single')}>
          Single
        </button>
        <button type="button" role="radio" aria-checked={draft.mode === 'range'} onClick={() => draft.setMode('range')}>
          Range
        </button>
      </div>
      {draft.mode === 'single' ? (
        <label className="field">
          <span className="label">Address</span>
          <input
            type="text"
            className="mono"
            value={draft.single}
            placeholder="10.0.0.5 or 10.0.0.0/24"
            spellCheck={false}
            onChange={(e) => draft.setSingle(e.target.value)}
          />
        </label>
      ) : (
        <>
          <label className="field">
            <span className="label">From</span>
            <input type="text" className="mono" value={draft.from} placeholder="10.0.0.1" spellCheck={false} onChange={(e) => draft.setFrom(e.target.value)} />
          </label>
          <label className="field">
            <span className="label">To</span>
            <input type="text" className="mono" value={draft.to} placeholder="10.0.0.50" spellCheck={false} onChange={(e) => draft.setTo(e.target.value)} />
          </label>
        </>
      )}
      <div className="filter-popover-actions">
        <button type="button" className="btn btn-ghost btn-sm" disabled={!value} onClick={() => onApply('')}>
          Clear
        </button>
        <button type="submit" className="btn btn-primary btn-sm" disabled={!draft.valid}>
          Apply
        </button>
      </div>
    </form>
  );
}

export function SourceIpFilter({ value, onChange }: { value: string; onChange: (value: string) => void }) {
  const popover = usePopover();
  const panelId = useId();
  const draft = useSourceIpDraft();

  const summary = value ? value.replace('-', ' – ') : 'All source IPs';

  function open() {
    if (!popover.open) draft.load(value);
    popover.toggle();
  }

  function apply(next: string) {
    onChange(next);
    popover.close();
  }

  return (
    <>
      <button
        ref={popover.button}
        type="button"
        className={`multi-select ip-filter has-lead${value ? ' has-value' : ''}`}
        aria-label={`Source IP: ${summary}`}
        aria-haspopup="dialog"
        aria-expanded={popover.open}
        aria-controls={popover.open ? panelId : undefined}
        onClick={open}
      >
        <Network className="select-lead-icon" aria-hidden="true" />
        <span className="multi-select-value">{summary}</span>
        <ChevronDown aria-hidden="true" />
      </button>
      {popover.open && (
        <div
          ref={popover.panel}
          id={panelId}
          className="user-dropdown filter-popover"
          role="dialog"
          aria-label="Source IP"
          style={popover.style}
        >
          <SourceIpForm draft={draft} value={value} onApply={apply} />
        </div>
      )}
    </>
  );
}
